import { PanelFilter, PanelFilterParams, QueryScope } from '../interfaces/panel-data';

/**
 * Builds the `filterParams` payload for a panel request from the scope, time window, and any extra
 * filters a panel supplies. This is the single place that decides what the backend filters on,
 * replacing the per-field request arguments the API used to take.
 *
 * `field` names must be ones the target panel allows; the backend rejects unknown fields rather than
 * ignoring them, so an unsupported dimension fails loudly instead of rendering an empty panel.
 *
 * Two omissions are deliberate. Drill-down state is *not* sent: a panel's aggregation tiers come back
 * in the response and expanding them is client-side, so filtering on a tier would hollow out the panel
 * it is meant to break down. And `scope.program` / `scope.subProgram` are not translated into filters
 * because the MARS metrics do not carry those attributes yet — filtering on them would match nothing.
 * Once MARS emits them, add them here and to the panel's allowlist together.
 */
export function buildPanelFilters(
  scope: QueryScope,
  options?: { timeWindow?: string; filters?: PanelFilter[] }
): PanelFilterParams {
  const filters: PanelFilter[] = [];

  if (scope.websiteId) {
    filters.push({ field: 'website_id', value: scope.websiteId });
  }
  if (options?.timeWindow) {
    filters.push({ field: 'time_window', value: options.timeWindow });
  }
  if (options?.filters?.length) {
    filters.push(...options.filters);
  }

  return { filters };
}

/** Serialises the payload for the wire, or returns undefined when there is nothing to send. */
export function serializePanelFilters(params: PanelFilterParams): string | undefined {
  return params.filters.length > 0 ? JSON.stringify(params) : undefined;
}
